import React, { useState, useCallback } from "react";
import { StyleSheet, ScrollView } from "react-native";
import { object, string } from "@lib/util/yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Text, View, Card, Wrapper } from "@components/atoms/Themed";
import Colors from "@constants/Colors";
import Fonts from "@constants/Fonts";
import ErrorDialog from "@components/molecules/dialog/ErrorDialog";
import InputAccessoryView from "@components/atoms/InputAccessoryView";
import KeyboardAvoidingView from "@components/molecules/KeyboardAvoidingView";
import { TextField, ErrorText } from "@components/molecules/TextInput";
import { graphql, useMutation } from "react-relay/hooks";
import NavigationBar from "@components/molecules/NavigationBar";
import { CampaignInputPhoneFormUpdateMutation } from "@generated/CampaignInputPhoneFormUpdateMutation.graphql";
import Spacer from "@components/atoms/Spacer";
import { KeyboardId } from "@constants/App";
import GradientButton from "@components/atoms/GradientButton";
import Divider from "@components/atoms/Divider";
import { resolveError } from "@lib/util/error";

const updateQuery = graphql`
  mutation CampaignInputPhoneFormUpdateMutation(
    $input: PhoneChangeMutationInput!
  ) {
    phoneChange(input: $input) {
      __typename
      ... on PhoneChangeError {
        errorType
        message
      }
    }
  }
`;

type PhoneInput = {
  newPhone: string;
};

export default function CampaignInputPhoneForm({
  onComplete,
  onClose,
}: {
  onComplete: (phone: string) => void;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [commit] =
    useMutation<CampaignInputPhoneFormUpdateMutation>(updateQuery);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<PhoneInput>({
    defaultValues: {
      newPhone: "",
    },
    mode: "onChange",
    resolver: yupResolver(
      object().shape({
        newPhone: string()
          .trim()
          .required("入力してください")
          .phoneNumber({
            message: "存在しない電話番号です",
          })
          .replace(/-/g, "")
          .toNull(),
      })
    ),
  });

  const onPress = useCallback(async () => {
    await handleSubmit(async ({ newPhone }: PhoneInput) => {
      try {
        setLoading(true);
        await new Promise<void>((resolve, reject) => {
          commit({
            variables: {
              input: {
                newPhone,
              },
            },
            onCompleted({ phoneChange }) {
              if (phoneChange.__typename === "PhoneChangeError") {
                reject(phoneChange.message);
              } else {
                resolve();
              }
            },
          });
        });

        onComplete(newPhone);
      } catch (e: unknown) {
        setError(resolveError(e).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [commit, handleSubmit, onComplete]);

  return (
    <View style={styles.container}>
      <NavigationBar onClose={onClose} title="電話番号登録" />
      <Spacer height={8} />
      <KeyboardAvoidingView>
        <ScrollView>
          <Wrapper>
            <Card style={styles.content}>
              <Text style={styles.message}>
                万が一の際に連絡が取れるように電話番号を入力してください。
              </Text>
            </Card>

            <Spacer height={16} />

            <Card style={styles.content}>
              <TextField
                control={control}
                inputAccessoryViewID={KeyboardId}
                label="電話番号"
                name="newPhone"
                placeholder="連絡がとれる電話番号を登録してください"
                type="phone"
              />
              {!isValid && errors.newPhone !== undefined && (
                <ErrorText error={errors.newPhone.message} />
              )}
              <Text style={styles.guide}>ハイフン(-)なし</Text>
            </Card>
          </Wrapper>
        </ScrollView>
      </KeyboardAvoidingView>

      <Divider />
      <Card style={styles.footer}>
        <GradientButton
          disabled={!isValid}
          loading={loading}
          onPress={onPress}
          title="電話番号を登録する"
        />
      </Card>
      {error !== null && (
        <ErrorDialog message={error} onClose={() => setError(null)} />
      )}
      <InputAccessoryView nativeID={KeyboardId} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  footer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  message: {
    ...Fonts.lb130,
    color: Colors.orange,
  },
  guide: {
    ...Fonts.lr130,
    color: Colors.gray,
  },
});
